import {
  collectPlaybookSections,
  isPlaybookSectionNode,
  type AdminApiPlaybook,
  type PlaybookId,
  type PlaybookNode,
  type PlaybookSectionNode,
} from "@repo/app-types/entities/playbook";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import { MultiSelectMenuPopover } from "@repo/ui/Menu/multiSelectMenuPopover";
import type {
  MultiSelectOption,
  MultiSelectSection,
} from "@repo/ui/Select/multiSelect";
import { useMemo, useState, type ReactNode } from "react";
import { MenuTrigger } from "react-aria-components";
import { useUpdateTeammateMutation } from "../../../api/entities/teammate.js";
import { useAppSelector } from "../../../hooks/hook.js";
import { selectTeammate } from "../../teammate/teammateSlice.js";
import { DisplayColumnsMenuTrigger } from "../displayColumnsMenuTrigger.js";

/**
 * Special ID used to represent the top-level Playbook column in the selector UI.
 */
const PLAYBOOK_COLUMN_ID = "__playbook__";

interface UseSectionColumnSelectorOptions {
  /** The playbook to get sections from */
  playbook: AdminApiPlaybook | undefined;
}

interface UseSectionColumnSelectorReturn {
  /** Whether the top-level Playbook column should be visible */
  showsPlaybookColumn: boolean;
  /** The sections that are currently selected to be displayed as columns */
  selectedSections: readonly PlaybookSectionNode[];
  /** The selector UI component */
  selector: ReactNode;
  /** The MultiSelect sections data for combining with other selectors */
  selectSections: readonly MultiSelectSection[];
  /** The currently selected IDs for combining with other selectors */
  selectedIds: readonly string[];
  /** The onChange handler for combining with other selectors */
  onSelectionChange: (keys: readonly string[]) => void;
}

/**
 * Hook that manages section column selection state and provides a dropdown UI.
 * Consolidates logic for fetching playbook sections, tracking selections, and rendering the selector.
 */
export function useSectionColumnSelector(
  options: UseSectionColumnSelectorOptions,
): UseSectionColumnSelectorReturn {
  const currentTeammate = useAppSelector(selectTeammate);
  const teammateId = currentTeammate?.id;
  const [updateTeammate] = useUpdateTeammateMutation();

  const { playbook } = options;
  const playbookId = playbook?.id;

  // Get all sections from the playbook
  const allSections = useMemo(() => {
    if (!playbook) {
      return [];
    }
    return collectPlaybookSections(playbook.assessment_tree);
  }, [playbook]);

  const initialSelectedIds = useMemo(() => {
    if (!currentTeammate || !playbookId) {
      return [PLAYBOOK_COLUMN_ID];
    }

    const preferences = currentTeammate.settings.preferences;

    // Filter out any invalid IDs (sections that no longer exist)
    const savedSectionIds =
      preferences.team_performance_section_column_ids?.[playbookId];
    const validSectionIds =
      savedSectionIds?.filter((id) =>
        allSections.some((section) => section.id === id),
      ) ?? [];

    // Playbook column visibility defaults to true
    const playbookColumnVisible =
      preferences.team_performance_playbook_column_visible?.[playbookId] ??
      true;

    return playbookColumnVisible
      ? [PLAYBOOK_COLUMN_ID, ...validSectionIds]
      : validSectionIds;
  }, [allSections, currentTeammate, playbookId]);

  const [selectedIds, setSelectedIds] =
    useState<readonly string[]>(initialSelectedIds);
  const [initializedParams, setInitializedParams] = useState<{
    teammateId: TeammateId | undefined;
    playbookId: PlaybookId | undefined;
  }>({ teammateId, playbookId });

  // If the teammate or playbook changes, shortcircuit rendering and reinitialize the state.
  if (
    initializedParams.teammateId !== teammateId ||
    initializedParams.playbookId !== playbookId
  ) {
    setSelectedIds(initialSelectedIds);
    setInitializedParams({ teammateId, playbookId });
  }

  const selectedSections = useMemo(() => {
    return allSections.filter((section) => selectedIds.includes(section.id));
  }, [allSections, selectedIds]);

  const handleSelectionChange = async (keys: readonly string[]) => {
    setSelectedIds(keys);

    if (!currentTeammate || !playbookId) {
      return;
    }

    const playbookColumnVisible = keys.includes(PLAYBOOK_COLUMN_ID);
    const sectionIds = keys.filter((id) => id !== PLAYBOOK_COLUMN_ID);

    try {
      const currentPreferences = currentTeammate.settings.preferences;

      await updateTeammate({
        id: currentTeammate.id,
        patchPayload: {
          preferences: {
            team_performance_section_column_ids: {
              ...currentPreferences.team_performance_section_column_ids,
              [playbookId]: sectionIds,
            },
            team_performance_playbook_column_visible: {
              ...currentPreferences.team_performance_playbook_column_visible,
              [playbookId]: playbookColumnVisible,
            },
          },
        },
      }).unwrap();
    } catch (error) {
      console.error("Failed to save column preferences:", error);
    }
  };

  const showsPlaybookColumn = selectedIds.includes(PLAYBOOK_COLUMN_ID);

  // Flatten section nodes into a flat list with indent levels,
  // with the root Playbook column as the first option
  const sectionOptions = useMemo(() => {
    if (!playbook) {
      return [];
    }
    const rootOption: MultiSelectOption = {
      id: PLAYBOOK_COLUMN_ID,
      label: playbook.name,
    };
    return [
      rootOption,
      ...traverseSectionOptions(playbook.assessment_tree.nodes, 1),
    ];
  }, [playbook]);

  const sections = useMemo(
    () => [
      {
        id: "playbook_sections",
        label: "Playbook adherence scores",
        options: sectionOptions,
      },
    ],
    [sectionOptions],
  );

  const selector =
    sectionOptions.length > 0 ? (
      <MenuTrigger>
        <DisplayColumnsMenuTrigger />
        <MultiSelectMenuPopover
          ariaLabel="Display columns"
          placement="bottom end"
          sections={sections}
          value={selectedIds}
          onChange={handleSelectionChange}
        />
      </MenuTrigger>
    ) : null;

  return {
    showsPlaybookColumn,
    selectedSections,
    selector,
    selectSections: sections,
    selectedIds,
    onSelectionChange: handleSelectionChange,
  };
}

/**
 * Iterates through playbook sections as select options with indent levels.
 */
function* traverseSectionOptions(
  nodes: readonly PlaybookNode[],
  depth: number,
): Iterable<MultiSelectOption> {
  for (const node of nodes) {
    if (isPlaybookSectionNode(node)) {
      yield { id: node.id, label: node.name, indent: depth };
      yield* traverseSectionOptions(node.nodes, depth + 1);
    }
  }
}
